/**
 * `useSiteScan` — dashboard hook for ADR-0184 enriched domain scan.
 *
 * Mirrors `useProfileExtraction` in structure:
 *   - On mount: check for an existing scan; if running, join the poll loop.
 *   - `trigger()`: POST to start a scan, then poll until done.
 *   - Polling: 3s interval, 90s max (LLM extraction can take 30–45s).
 *   - `isAvailable` flips false on 404 (feature flag off).
 *   - Never throws — every error path is fail-soft.
 */

import { useCallback, useEffect, useRef, useState } from 'react';

import type { SiteScanRow } from '@sill/types/api';
import type { SiteId } from '@sill/types';

import { useApi } from '../api/api-context.js';
import { ApiError } from '../api/http-client.js';

export type SiteScanUiStatus = 'idle' | 'running' | 'completed' | 'failed';

export interface UseSiteScanState {
  status: SiteScanUiStatus;
  scan: SiteScanRow | null;
  isAvailable: boolean;
  trigger: () => Promise<void>;
}

const POLL_INTERVAL_MS = 3_000;
const POLL_MAX_MS = 90_000;

export function useSiteScan(
  site_id: SiteId | null | undefined,
): UseSiteScanState {
  const api = useApi();
  const [status, setStatus] = useState<SiteScanUiStatus>('idle');
  const [scan, setScan] = useState<SiteScanRow | null>(null);
  const [isAvailable, setIsAvailable] = useState<boolean>(true);

  const pollTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  const pollStartedAt = useRef<number>(0);
  const mounted = useRef<boolean>(true);

  const clearPoll = useCallback((): void => {
    if (pollTimer.current !== null) {
      clearInterval(pollTimer.current);
      pollTimer.current = null;
    }
  }, []);

  const applyScanRow = useCallback(
    (row: SiteScanRow): void => {
      if (!mounted.current) return;
      setScan(row);
      if (row.status === 'completed') {
        clearPoll();
        setStatus('completed');
      } else if (row.status === 'failed') {
        clearPoll();
        setStatus('failed');
      } else {
        setStatus('running');
      }
    },
    [clearPoll],
  );

  const markFailed = useCallback((): void => {
    if (!mounted.current) return;
    clearPoll();
    setStatus('failed');
  }, [clearPoll]);

  const beginPolling = useCallback((): void => {
    if (site_id === null || site_id === undefined) return;
    clearPoll();
    pollStartedAt.current = Date.now();
    const tick = async (): Promise<void> => {
      if (!mounted.current || site_id === null || site_id === undefined) {
        clearPoll();
        return;
      }
      if (Date.now() - pollStartedAt.current > POLL_MAX_MS) {
        markFailed();
        return;
      }
      try {
        const row = await api.scan.getLatest(site_id);
        if (!mounted.current) return;
        if (row === null) {
          clearPoll();
          setStatus('idle');
          setIsAvailable(false);
          return;
        }
        applyScanRow(row);
      } catch {
        markFailed();
      }
    };
    pollTimer.current = setInterval(() => {
      void tick();
    }, POLL_INTERVAL_MS);
    void tick();
  }, [api, applyScanRow, clearPoll, markFailed, site_id]);

  const trigger = useCallback(async (): Promise<void> => {
    if (site_id === null || site_id === undefined) return;
    setStatus('running');
    setScan(null);
    try {
      const ack = await api.scan.trigger(site_id);
      if (!mounted.current) return;
      if (ack === null) {
        setStatus('idle');
        setIsAvailable(false);
        return;
      }
      beginPolling();
    } catch (err) {
      if (!mounted.current) return;
      // 429 rate-limited — surface it distinctly so the UI can show
      // "scan limit reached" rather than generic fail. Keep status as
      // running until we can show the right state.
      if (err instanceof ApiError && err.kind === 'rate_limited') {
        setStatus('failed');
        setScan(null);
      } else {
        markFailed();
      }
    }
  }, [api, beginPolling, markFailed, site_id]);

  useEffect(() => {
    mounted.current = true;
    if (site_id === null || site_id === undefined) {
      return (): void => {
        mounted.current = false;
      };
    }
    let cancelled = false;
    void (async (): Promise<void> => {
      try {
        const row = await api.scan.getLatest(site_id);
        if (cancelled || !mounted.current) return;
        if (row === null) return;
        applyScanRow(row);
        if (row.status === 'running') {
          beginPolling();
        }
      } catch {
        // On-mount errors are non-blocking — the feature may not be
        // enabled yet; we leave status 'idle' and the UI hides.
      }
    })();
    return (): void => {
      cancelled = true;
      mounted.current = false;
      clearPoll();
    };
  }, [api, applyScanRow, beginPolling, clearPoll, site_id]);

  return { status, scan, isAvailable, trigger };
}
